import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Zap, TrendingUp, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const HERO_IMG = "https://images.pexels.com/photos/6256754/pexels-photo-6256754.jpeg?auto=compress&cs=tinysrgb&w=1920";

const features = [
  { icon: Target, title: "Precision Matching", desc: "AI analyzes your swing profile against real shaft specs" },
  { icon: Zap, title: "Live Product Data", desc: "Crawls grips4less.com in real-time for current pricing" },
  { icon: TrendingUp, title: "Expert Analysis", desc: "Explains why each shaft suits your game" },
  { icon: ShieldCheck, title: "Trusted Source", desc: "All products sourced from grips4less.com" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#03170D]">
      {/* Hero */}
      <section className="hero-bg relative min-h-screen flex items-center justify-center">
        <img
          src={HERO_IMG}
          alt="Golf course at twilight"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p
              data-testid="hero-label"
              className="text-sm uppercase tracking-[0.2em] font-medium text-[#D4AF37] mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              More Distance. More Fairways. More Confidence.
            </p>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-white mb-6"
            >
              Find Your Perfect Shaft
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-base sm:text-lg text-[#A3B8AA] mb-10 max-w-xl mx-auto leading-relaxed"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Tell us about your game, your goals, and what you&#39;re looking for in a shaft.
              We&#39;ll match you with personalized recommendations from the largest collection
              of golf shafts online — only at grips4less.com.
            </p>
            <Button
              data-testid="start-questionnaire-btn"
              onClick={() => navigate("/questionnaire")}
              className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold text-base px-8 py-6 rounded-full transition-all hover:-translate-y-0.5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Find My Perfect Shaft
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-4 max-w-5xl mx-auto">
        <h2
          data-testid="features-heading"
          className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-center text-white mb-16"
        >
          How It Works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 text-center"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[rgba(212,175,55,0.15)] flex items-center justify-center">
                <f.icon className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3
                className="text-lg font-medium text-white mb-2"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                {f.title}
              </h3>
              <p className="text-sm text-[#A3B8AA]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1A4026] py-8 px-4 text-center">
        <p className="text-sm text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Products sourced from{" "}
          <a
            href="https://www.grips4less.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#D4AF37] hover:text-[#F0CC5C] transition-colors"
          >
            grips4less.com
          </a>
        </p>
      </footer>
    </div>
  );
}
